import { ReadOnlyField, SimpleGrid, Stack } from '@agrotoken-tech/ui';
import { useToastsHook, useTranslation } from '@bcba/react-hooks';
import { CommonCropPledgeDebtorInformationSerializer } from '@bcba/serializers';
import {
  QueryObserverResult,
  RefetchOptions,
  useMutation,
} from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { patchApproveCropPledgeDebtorInformation } from '../../../../services/crop-pledge';
import { CropPledgeSectionActions } from '../../shared/CropPledgeSectionActions';
import { useCropPledgeContext } from '../context/CropPledgeContext';

interface Props {
  data: CommonCropPledgeDebtorInformationSerializer;
  refetch: (
    options?: RefetchOptions,
  ) => Promise<
    QueryObserverResult<CommonCropPledgeDebtorInformationSerializer, Error>
  >;
}

export const PledgeDebtorPJForm = ({
  data: {
    fiscalId,
    igjIdentifier,
    streetName,
    streetNumber,
    province,
    locality,
    postalCode,
    email,
    phoneNumber,
    companyName,
  },
  refetch: refetchDebtorData,
}: Props) => {
  const { t } = useTranslation();
  const { uuid = '' } = useParams();
  const { showSuccessToast } = useToastsHook();

  const { cropPledgeStatus, refetchCropPledgeStatus } = useCropPledgeContext();

  const { sections } = cropPledgeStatus ?? {};
  const { debtor: debtorSection } = sections ?? {};

  const { mutateAsync: approveSection, isPending: isPendingSectionApproval } =
    useMutation({
      mutationFn: () => patchApproveCropPledgeDebtorInformation(uuid),
      onSuccess: () => {
        showSuccessToast({ title: t('common.approve.toast.success') });
        refetchDebtorData();
        refetchCropPledgeStatus();
      },
    });

  return (
    <Stack>
      <SimpleGrid columns={2} mt="6" spacing={6}>
        <ReadOnlyField label={t('common.CUIT')} value={fiscalId} />
        <ReadOnlyField
          label={t('common.companyName')}
          value={companyName || ''}
        />
        <ReadOnlyField label={t('common.IGJ')} value={igjIdentifier || ''} />
        <ReadOnlyField
          label={t('businessSignUp.form.addressStreet.label')}
          value={streetName || ''}
        />
        <ReadOnlyField
          label={t('businessSignUp.form.addressNumber.label')}
          value={streetNumber || ''}
        />
        <ReadOnlyField
          label={t('businessSignUp.form.state.label')}
          value={province || ''}
        />
        <ReadOnlyField
          label={t('businessSignUp.form.locality.label')}
          value={locality || ''}
        />
        <ReadOnlyField
          label={t('businessSignUp.form.zipCode.label')}
          value={postalCode || ''}
        />
        <ReadOnlyField label={t('common.email')} value={email || ''} />
        <ReadOnlyField label={t('common.phone')} value={phoneNumber || ''} />
      </SimpleGrid>
      <CropPledgeSectionActions
        sectionStatus={debtorSection!.status}
        onApprove={() => approveSection()}
        isPendingApprove={isPendingSectionApproval}
      />
    </Stack>
  );
};
